"use client";

import type { TeamAgent } from "@bash/shared";
import type { ComposerHistoryEntry } from "@/stores/composerHistoryStore";
import { cn } from "@/lib/utils";
import { BashTurn } from "./BashTurn";
import { CopyButton } from "./CopyButton";
import { Glyph } from "./glyphs";
import { HistoryTime, MetaDot } from "./HistoryTime";
import { SourceMark } from "./SourceMark";
import { sourceOf } from "./sourceOf";

/** His ask: the bubble on the right, and under it where it came from, the
 *  time and Copy. A failed ask is outlined red with "Not sent · Try again";
 *  the server's own message is never shown. */
function HisAsk({
  entry,
  onRetry,
}: {
  entry: ComposerHistoryEntry;
  onRetry: (words: string) => void;
}) {
  const failed = entry.kind === "error";
  const words = entry.question;
  const source = failed ? null : sourceOf(entry);
  return (
    <div className="group/turn flex flex-col items-end gap-1">
      <div
        className={cn(
          "max-w-[min(78%,460px)] rounded-16 rounded-br-6 bg-bg-muted px-3 py-2 text-3.5 leading-normal whitespace-pre-wrap text-fg-default wrap-anywhere",
          failed &&
            "bg-transparent text-fg-muted ring-1 ring-kind-red/50 ring-inset",
          !words && "text-fg-subtle"
        )}
        data-testid="history-ask"
        data-failed={failed ? "" : undefined}
      >
        {words || "Sent a photo"}
      </div>
      <div className="flex h-5.5 items-center gap-1 pr-1 text-3 whitespace-nowrap text-fg-subtle">
        {words && <CopyButton text={words} label="Copy your message" />}
        {failed ? (
          <>
            <span className="inline-flex items-center gap-1 font-medium text-kind-red">
              <Glyph name="alert" className="size-[13px]" />
              Not sent
            </span>
            {words && (
              <>
                <MetaDot />
                <button
                  type="button"
                  onClick={() => onRetry(words)}
                  className="-mx-1 cursor-pointer rounded-4 border-0 bg-transparent px-1 py-0.5 text-3 font-semibold text-fg-default underline decoration-border-muted underline-offset-2 hover:decoration-current"
                >
                  Try again
                </button>
              </>
            )}
          </>
        ) : (
          <>
            {source && (
              <>
                <SourceMark source={source} />
                <MetaDot />
              </>
            )}
            <HistoryTime ts={entry.ts} />
          </>
        )}
      </div>
    </div>
  );
}

/**
 * One row of the history as a chat (spec 2026-09-24 §1): his ask, then
 * Bash's turn; or Bash's turn alone when Bash spoke first (a report). A
 * failed ask has no turn of Bash's.
 */
export function HistoryExchange({
  entry,
  onGo,
  onRetry,
  speaker,
}: {
  entry: ComposerHistoryEntry;
  /** Open a route in the app, then close the history. */
  onGo: (route: string) => void;
  /** Send these words again as a new ask. */
  onRetry: (words: string) => void;
  /** Agents first (train 1b): the agent answering in this transcript; absent = Bash. */
  speaker?: Pick<TeamAgent, "id" | "kind" | "name">;
}) {
  const bashFirst = entry.author === "bash";
  return (
    <div
      className="flex flex-col gap-2.5"
      data-testid="history-exchange"
      data-kind={entry.kind}
    >
      {!bashFirst && <HisAsk entry={entry} onRetry={onRetry} />}
      {entry.kind !== "error" && (
        <BashTurn
          entry={entry}
          onGo={onGo}
          showTime={bashFirst}
          speaker={speaker}
        />
      )}
    </div>
  );
}
